import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/components/Icon';
import { RequireRole } from '@/components/RequireRole';
import { Banner, Dim, EmptyState, ErrorPage, LoadingPage, Stat } from '@/components/primitives';
import { AlertFeedRow } from '@/components/alerts/AlertFeedRow';
import { RuleCard } from '@/components/alerts/RuleCard';
import { RuleEditor } from '@/components/alerts/RuleEditor';
import { keys, useAlerts, useChannels, useNoiseBudget, useRules, useWorkflows } from '@/hooks/useSturmData';
import { notificationsApi } from '@/lib/apiClient';
import type { NotificationRule } from '@/types/sturm';

type Tab = 'rules' | 'feed' | 'channels';

export function NotificationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: rules = [], isLoading, isError, error } = useRules();
  const { data: alerts = [] } = useAlerts();
  const { data: channels = [] } = useChannels();
  const { data: workflows = [] } = useWorkflows();
  const { data: budget } = useNoiseBudget();

  const [tab, setTab] = useState<Tab>('rules');
  const [editing, setEditing] = useState<NotificationRule | 'new' | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: keys.rules });

  const toggle = useMutation({
    mutationFn: (rule: NotificationRule) =>
      notificationsApi.updateRule(rule.id, { isEnabled: !rule.isEnabled }),
    onSuccess: invalidate,
  });

  const save = useMutation({
    mutationFn: (draft: Partial<NotificationRule>) =>
      draft.id ? notificationsApi.updateRule(draft.id, draft) : notificationsApi.createRule(draft),
    onSuccess: () => {
      invalidate();
      setEditing(null);
    },
  });

  const testChannel = useMutation({ mutationFn: (id: string) => notificationsApi.testChannel(id) });

  if (isLoading) {
    return <LoadingPage label="Loading notification rules…" />;
  }

  if (isError) {
    return (
      <ErrorPage
        title="Could not load notifications"
        message={error instanceof Error ? error.message : 'The API did not respond.'}
      />
    );
  }

  const firing = alerts.filter((a) => a.state === 'firing');
  const workflowNames = Object.fromEntries(workflows.map((w) => [w.slug, w.name]));

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Notifications</div>
          <div className="page-sub">
            One place to decide what a problem is and who hears about it. Rules watch the run stream
            directly — no external monitor to keep in sync.
          </div>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={() => setTab('channels')}>
            <Icon name="users" size={14} /> Channels
          </button>
          <RequireRole min="manager">
            <button className="btn btn-accent" onClick={() => setEditing('new')}>
              <Icon name="plus" size={14} /> New rule
            </button>
          </RequireRole>
        </div>
      </div>

      <div className="grid-4 mb-24">
        <Stat
          icon="alert"
          label="Firing now"
          value={firing.length}
          tone={firing.length ? 'error' : undefined}
          meta={severityBreakdown(firing.map((a) => a.severity))}
        />
        <Stat
          icon="bell"
          label="Active rules"
          value={
            <>
              {rules.filter((r) => r.isEnabled).length}
              <Dim> / {rules.length}</Dim>
            </>
          }
          meta={`across ${workflows.length} workflows`}
        />
        <Stat
          icon="send"
          label="Sent · 24h"
          value={budget?.afterThrottle ?? 0}
          meta={`grouped from ${budget?.raw ?? 0} events`}
        />
        <Stat icon="clock" label="Paged · 24h" value={budget?.paged ?? 0} meta="critical severity only" />
      </div>

      <div className="tabs">
        <div className={`tab ${tab === 'rules' ? 'active' : ''}`} onClick={() => setTab('rules')}>
          <Icon name="bell" size={14} /> Rules <span className="count">{rules.length}</span>
        </div>
        <div className={`tab ${tab === 'feed' ? 'active' : ''}`} onClick={() => setTab('feed')}>
          <Icon name="logs" size={14} /> Delivered <span className="count">{alerts.length}</span>
        </div>
        <div className={`tab ${tab === 'channels' ? 'active' : ''}`} onClick={() => setTab('channels')}>
          <Icon name="hash" size={14} /> Channels <span className="count">{channels.length}</span>
        </div>
      </div>

      {tab === 'rules' ? (
        <>
          {editing ? (
            <RuleEditor
              rule={editing === 'new' ? null : editing}
              channels={channels}
              workflows={workflows}
              onClose={() => setEditing(null)}
              onSave={(draft) => save.mutate(draft)}
              isSaving={save.isPending}
            />
          ) : null}
          <div className="stack gap-12">
            {rules.length === 0 ? (
              <div className="card">
                <EmptyState
                  title="No rules yet"
                  sub="Without a rule, a failing workflow waits to be noticed. Start with “run failed” on the workflows you care about."
                />
              </div>
            ) : (
              rules.map((rule) => (
                <RuleCard
                  key={rule.id}
                  rule={rule}
                  channels={channels}
                  workflowNames={workflowNames}
                  onToggle={() => toggle.mutate(rule)}
                  onEdit={() => setEditing(rule)}
                />
              ))
            )}
          </div>
        </>
      ) : null}

      {tab === 'feed' ? (
        <div className="grid-2-1">
          <div className="card">
            <div className="card-hd">
              <div className="flex1">
                <div className="card-hd-title">Delivered notifications</div>
                <div className="card-hd-sub">Every message Sturm sent, and what it was grouped from.</div>
              </div>
            </div>
            {alerts.length === 0 ? (
              <EmptyState title="Nothing delivered yet" sub="Alerts appear here once a rule matches." />
            ) : (
              <div>
                {alerts.map((alert) => (
                  <AlertFeedRow
                    key={alert.id}
                    alert={alert}
                    channels={channels}
                    workflowName={workflowNames[alert.workflowSlug ?? '']}
                    onClick={() =>
                      alert.workflowSlug && navigate(`/workflows/${alert.workflowSlug}?tab=alerts`)
                    }
                  />
                ))}
              </div>
            )}
          </div>
          <div className="stack gap-16">
            <div className="card">
              <div className="card-hd">
                <div className="card-hd-title">Noise budget</div>
              </div>
              <div className="card-pad kv">
                <BudgetRow k="Raw events" v={budget?.raw ?? 0} />
                <BudgetRow k="After grouping" v={budget?.afterGrouping ?? 0} />
                <BudgetRow k="After throttle" v={budget?.afterThrottle ?? 0} />
                <BudgetRow k="Paged" v={budget?.paged ?? 0} />
                <BudgetRow k="Muted by you" v={budget?.muted ?? 0} />
              </div>
            </div>
            <Banner>
              <strong>Grouping is doing the work.</strong> A burst of identical failures becomes one
              message that counts up as it repeats, rather than one message per run.
            </Banner>
          </div>
        </div>
      ) : null}

      {tab === 'channels' ? (
        <div className="grid-2">
          {channels.length === 0 ? (
            <div className="card">
              <EmptyState
                title="No channels configured"
                sub="Add an email recipient or a webhook so rules have somewhere to deliver."
              />
            </div>
          ) : (
            channels.map((channel) => (
              <div key={channel.id} className="card">
                <div className="card-pad row gap-16">
                  <div className="tile lg" style={{ background: 'var(--ink-900)', color: 'var(--white)' }}>
                    <Icon
                      name={channel.kind === 'webhook' ? 'link' : channel.kind === 'email' ? 'mail' : 'hash'}
                      size={18}
                    />
                  </div>
                  <div className="flex1">
                    <div className="row gap-8">
                      <div style={{ fontWeight: 600 }}>{channel.name}</div>
                      {channel.isConfigured ? (
                        <span className="badge badge-success">
                          <span className="dot" />
                          Connected
                        </span>
                      ) : (
                        <span className="badge badge-warn">
                          <span className="dot" />
                          Not configured
                        </span>
                      )}
                    </div>
                    <div className="tiny muted">
                      {channel.description ?? channel.provider} · used by {channel.ruleCount} rules
                      {channel.supportsUpdate ? ' · edits in place' : ''}
                    </div>
                  </div>
                  <RequireRole min="admin">
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => testChannel.mutate(channel.id)}
                      disabled={testChannel.isPending || !channel.isConfigured}
                    >
                      <Icon name="send" size={12} /> Test
                    </button>
                  </RequireRole>
                </div>
              </div>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

function BudgetRow({ k, v }: { k: string; v: number }) {
  return (
    <div className="kv-row">
      <span className="kv-k">{k}</span>
      <span className="kv-v">{v.toLocaleString()}</span>
    </div>
  );
}

function severityBreakdown(severities: string[]): string {
  if (severities.length === 0) {
    return 'nothing firing';
  }

  const counts = severities.reduce<Record<string, number>>((acc, s) => {
    acc[s] = (acc[s] ?? 0) + 1;
    return acc;
  }, {});

  return Object.entries(counts)
    .map(([severity, count]) => `${count} ${severity}`)
    .join(' · ');
}
